import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d52pdiabfdatfusc40p0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          asunto: '',
          mensaje: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contacto" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/4d08b92567dcb193087bfbd74b5ee8a8.jpg"
          alt="Oficina Legal"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Side - Info */}
          <div>
            <h2 className="font-serif font-bold text-5xl lg:text-6xl text-white leading-tight mb-8">
              Proteja Sus Derechos Con Experiencia Comprobada
            </h2>
            <p className="text-gray-200 text-lg leading-relaxed mb-8">
              Primera consulta gratuita. Respuesta en menos de 24 horas. Atención personalizada y confidencial.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-gold text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-300 text-sm mb-1">Teléfono</div>
                  <a href="tel:+50671075130" className="text-white text-lg font-semibold hover:text-gold transition-colors">
                    +506 7107-5130
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-gold text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-300 text-sm mb-1">Email</div>
                  <a href="mailto:dsabogadocr@gmail.com" className="text-white text-lg font-semibold hover:text-gold transition-colors">
                    dsabogadocr@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gold/20 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-gold text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-300 text-sm mb-1">Oficina</div>
                  <p className="text-white text-lg font-semibold">
                    Cartago, Costa Rica
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-2xl">
            <h3 className="font-serif font-bold text-3xl text-gray-900 mb-2">
              Solicite una Consulta
            </h3>
            <p className="text-gray-600 text-sm mb-8">
              Complete el formulario y me pondré en contacto con usted a la brevedad.
            </p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  required
                  value={formData.nombre}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Juan Pérez"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Correo Electrónico *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all text-sm"
                  placeholder="juan@ejemplo.com"
                />
              </div>

              <div>
                <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  required
                  value={formData.telefono}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all text-sm"
                  placeholder="+506 7107-5130"
                />
              </div>

              <div>
                <label htmlFor="asunto" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Consulta *
                </label>
                <select
                  id="asunto"
                  name="asunto"
                  required
                  value={formData.asunto}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Seleccione una opción</option>
                  <option value="Defensa Penal">Defensa Penal</option>
                  <option value="Asesoría Corporativa">Asesoría Corporativa</option>
                  <option value="Litigios Civiles">Litigios Civiles</option>
                  <option value="Delitos Económicos">Delitos Económicos</option>
                  <option value="Derecho Laboral">Derecho Laboral</option>
                  <option value="Derecho de Familia">Derecho de Familia</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                  Mensaje *
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  required
                  rows={4}
                  maxLength={500}
                  value={formData.mensaje}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="Describa brevemente su situación legal..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.mensaje.length}/500 caracteres
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-navy text-white text-base font-semibold rounded-full hover:bg-navy-light transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Consulta'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm text-center">
                    ¡Mensaje enviado con éxito! Me pondré en contacto pronto.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm text-center">
                    Hubo un error al enviar el mensaje. Por favor, intente nuevamente.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
